import {Reservation} from '../../model/reservation/reservation.model';
import {dateDifference, useDefault} from '../../../common/common';
import {GuestAddress} from '../../model/guest/guest-address.model';
import {Guest} from '../../model/guest/guest.model';
import {Stay} from '../../model/reservation/stay.model';
import {DateUtils} from '../../../common/date.utils';
import {Room} from '../../model/reservation/room/room.model';
import {Rate} from '../../model/reservation/rate/rate.model';
import {RateCharge} from '../../model/reservation/rate/rate-charge.model';
import {ChargeType} from '../../model/reservation/rate/charge.type.model';
import {RateChange} from '../../model/reservation/rate/rate-change.model';
import {CancellationPolicy} from '../../model/reservation/rate/cancellation-policy.model';
import {RateCharges} from '../../model/reservation/rate/rate-charges.model';
import {RateRule} from '../../model/reservation/rate/rate-rule.model';
import {RateRuleType} from '../../model/reservation/rate/rate-rule.type.model';
import {DepositPolicy} from '../../model/reservation/rate/deposit-policy.model';
import {DepositPolicyType} from '../../model/reservation/rate/deposit-policy.type.model';
import {AdditionalGuestRestriction} from "../../model/reservation/rate/additional-guest-restriction.model";

const ZERO = 0;
export class ReservationModelBuilder {
  private _model: Reservation;
  constructor() {
    this._model = new Reservation();
  }

  withReservationData(reservationData: any) {
    this._model.confirmationNumber = reservationData.confirmationNumbers && reservationData.confirmationNumbers.length ? reservationData.confirmationNumbers[0].ihgConfirmationNumber : null
    this._model.cancellationNumber = useDefault(reservationData.cancellationNumber, null);
    this._model.status = useDefault(reservationData.status, '');
    this._model.guest = this._buildGuest(useDefault(reservationData.guest, {}));
    this._model.additionalGuests = this._buildAdditionalGuests(useDefault(reservationData.guest, {}).additionalGuests);
    this._model.originalSellDate = useDefault(reservationData.originalSellDate, '');
    const stayData = useDefault(reservationData.stay, {});
    this._model.stay = this._buildStay(stayData);
    this._model.hotelMnemonic = useDefault(stayData.hotelMnemonic, '');
    this._model.room.code = useDefault(stayData.roomCode, '');
    this._model.rate.code = useDefault(stayData.rateCode, '');
    this._model.corporateId = useDefault(useDefault(reservationData.discounts, {}).corporateId, null);
    this._model.communicationPreferences = useDefault(reservationData.communicationPreferences, {});
    this._model.specialNightRate = useDefault(reservationData.specialNightRate, {});
    return this;
  }

  withEnrollmentData(enrollmentData: any) {
    return this.withReservationData(useDefault(enrollmentData, {}).hotelReservation);
  }

  withRateRulesData(rateRulesData) {
    const defaultRateRulesData = useDefault(rateRulesData, {});
    const roomData = useDefault(defaultRateRulesData.room, {});
    const rateData = useDefault(defaultRateRulesData.rate, {});
    this._model.room = this._buildRoom(roomData);
    this._model.rate = this._buildRate(rateData, roomData, useDefault(defaultRateRulesData.notes, []));
    this._model.disclaimer = useDefault(defaultRateRulesData.disclaimer, []);
    return this;
  }
  /*
    This is needed because the reservation service does not return back the group code when checking availability,
     so we need to patch the response
   */
  withGroupCodeData(reservation: Reservation, rateRulesData, groupCode) {
    const rateData = useDefault(rateRulesData.rate, {});
    if (reservation.rate.groupCode === rateData.rateCode) {
      this._model.rate.groupCode = reservation.rate.groupCode;
    } else if (groupCode) {
      this._model.rate.groupCode = groupCode;
    }
    return this;
  }

  build (): Reservation {
    return this._model;
  }

  private _buildStay(stayData: any): Stay {
    const stay: Stay = new Stay();
    stay.numberOfChildren = useDefault(stayData.children, 0);
    stay.numberOfAdults = useDefault(stayData.adults, 0);
    stay.numberOfRooms = useDefault(stayData.numberOfRooms, 0);
    stay.checkInDate = DateUtils.fromISOToDate(stayData.checkInDate);
    stay.checkOutDate = DateUtils.fromISOToDate(stayData.checkOutDate);
    stay.numberOfNights = useDefault(stayData.numberOfNights, dateDifference(stayData.checkInDate, stayData.checkOutDate));
    stay.totalPrice = useDefault(stayData.totalPrice, null);
    return stay;
  }

  private _buildAdditionalGuests(additionalGuestsData): Array<Guest>{
    const additionalGuests: Array<Guest> = new Array<Guest>();
    for (const additionalGuestData of useDefault(additionalGuestsData, [])) {
      additionalGuests.push(this._buildGuest(useDefault(additionalGuestData, {})));
    }
    return additionalGuests;
  }

  private _buildGuest(guestData): Guest {
    const guest: Guest =  new Guest(useDefault(guestData.firstName, ''), useDefault(guestData.lastName, ''), useDefault(guestData.rewardsClubNumber, ''));
    guest.phoneNumber = useDefault(guestData.phoneNumber, '');
    guest.address = this._buildGuestAddress(useDefault(guestData.address, {}));
    return guest;
  }

  private _buildGuestAddress(guestAddressData): GuestAddress {
    const address: GuestAddress = new GuestAddress();
    address.countryCode = useDefault(guestAddressData.countryCode, '');
    address.stateCode = useDefault(guestAddressData.stateCode, '');
    address.addressLine1 = useDefault(guestAddressData.addressLine1, '');
    address.addressLine2 = useDefault(guestAddressData.addressLine2, '');
    address.addressLine3 = useDefault(guestAddressData.addressLine3, '');
    address.postalCode = useDefault(guestAddressData.postalCode, '');
    address.city = useDefault(guestAddressData.city, '');
    return address;
  }

  private _buildRoom(roomData): Room {
    const room: Room = new Room();
    room.code = useDefault(roomData.roomCode, '');
    room.description = useDefault(roomData.description, '');
    room.maxNumberOfPeople = useDefault(roomData.maxPeople, 0);
    room.smoking = useDefault(roomData.smoking, false);
    room.wheelchairAccessible = useDefault(roomData.wheelchairAccessible, false);
    room.numRoomsAvailable = useDefault(roomData.numRoomsAvailable, 0);
    room.currencyCode = useDefault(roomData.currencyCode, '');
    return room;
  }

  private _buildRate(rateData: any, roomData: any, notesData: any): Rate {
    const rate: Rate = new Rate();
    rate.code = useDefault(rateData.rateCode, '');
    rate.name = useDefault(rateData.shortDescription, '');
    rate.desc = useDefault(rateData.longDescription, '');
    rate.currencyCode = useDefault(rateData.currencyCode, '');
    rate.totalPointsCost = useDefault(rateData.totalPointsCost, null);
    rate.earlyDepartureFee = useDefault(rateData.earlyDepartureFee, null);
    rate.checkInTime = useDefault(rateData.checkInTime, null);
    rate.checkOutTime = useDefault(rateData.checkOutTime, null);
    rate.isRewardNight = useDefault(rateData.rewardNightPackage, false);
    rate.freeNight = useDefault(rateData.freeNight, false);
    rate.groupCode = useDefault(rateData.groupCode, null);
    rate.memberExclusive = useDefault(this._isMemberExclusive(useDefault(rateData.marketingIndicator, null), useDefault(rateData.rateTags, [])), false);
    this._setCancellationPolicy(rate, useDefault(rateData.cancellationPolicy, null), useDefault(rateData.secondaryCancellationPolicies, []));
    this._setCharges(rate, rateData, roomData);
    this._setRules(rate, rateData);
    if(rateData.depositPolicy){
      rate.depositPolicy = new DepositPolicy(DepositPolicyType.DEFAULT_DEPOSIT, rateData.depositPolicy);
    } else {
      this._setDepositPolicy(rate, rateData, notesData);
    }
    if (rateData.additionalGuestRestriction) {
      this._setAdditionalGuestRestriction(rate, useDefault(rateData.additionalGuestRestriction.body, null), useDefault(rateData.additionalGuestRestriction.header, null));
    }
    return rate;
  }

  private _setAdditionalGuestRestriction(rate: Rate, body: string, header: string) {
    rate.additionalGuestRestriction = new AdditionalGuestRestriction(body, header);
  };

  private _isMemberExclusive(marketingIndicator, rateTags) {
    return marketingIndicator === 'E' || marketingIndicator === 'L' || (rateTags.indexOf('MEMBER') !== -1);
  }

  private _setCancellationPolicy(rate: Rate, primaryCancellationPolicy: string, secondaryCancellationPolicy: Array<string>) {
    rate.cancellationPolicy =  new CancellationPolicy(primaryCancellationPolicy, secondaryCancellationPolicy);
  }

  private _setCharges(rate: Rate, rateData, roomData) {
    const chargesData = useDefault(roomData.charges, {});
    this._setRateCharges(rate, useDefault(rateData.charges, []));
    this._setRateChanges(rate, useDefault(rateData.rateChanges, []));
    this._setOriginalRateChanges(rate, useDefault(rateData.rateChanges, []));
    let avgNightlyRate = chargesData.avgNightlyRate;
    let total = chargesData.total;
    let price = chargesData.price;
    let taxes = chargesData.taxes;
    if (rateData.rewardNightPackage) {
      const dailyPointsCost = Number(useDefault(rateData.dailyPointsCost, 0));
      const totalPointsCost = Number(useDefault(rateData.totalPointsCost, 0));
      avgNightlyRate = dailyPointsCost;
      price = totalPointsCost;
      total = totalPointsCost;
      rate.rateChanges = new Array<RateChange>();
      rate.rateChanges.push(new RateChange(dailyPointsCost, this._model.stay.checkInDate, this._model.stay.checkOutDate))
    }
    if (rateData.rewardNightPackage || rateData.freeNight) {
      taxes = ZERO;
    }
    rate.charges = new RateCharges(avgNightlyRate, price, taxes, total, chargesData.serviceCharges, chargesData.extraPersonCharge, chargesData.taxesIncludingXPC);
  }

  private _setRateCharges(rate: Rate, chargesData: any) {
    for (const charge of chargesData) {
      if (charge.subTypeStr.indexOf('SERVICE') !== -1) {
        rate.serviceCharges.push(new RateCharge(ChargeType.SERVICE, charge.amount, charge.description, charge.text, charge.includedInPrice));
      } else if (charge.subTypeStr.indexOf('TAX') !== -1) {
        rate.taxCharges.push(new RateCharge(ChargeType.TAX, charge.amount, charge.description, charge.text, charge.includedInPrice));
      }
    }
  }

  private _setRules(rate: Rate, rateData: any) {
    if (rateData.rateTags) {
      if (rateData.rateTags.indexOf('ADVANCED_PURCHASE_REQUIRED') !== -1) {
        rate.rules.push(new RateRule(RateRuleType.ADVANCED_PURCHASE));
      }
      rate.refundable = rateData.rateTags.indexOf('REFUNDABLE') !== -1;
    }
    if (rateData.rateInfos) {
      const advBook = rateData.rateInfos.find(element => element.type === 'ADVANCED_BOOKING_REQUIRED');
      if (!!advBook) {
        rate.rules.push(new RateRule(RateRuleType.ADVANCED_BOOKING, advBook.description));
      }
    }
  }

  private _setDepositPolicy(rate: Rate, rateData: any, notesData: any) {
    const note = notesData.find(element => element.type === 'DEPOSIT');
    if (note) {
      if (note.data && Array.isArray(note.data) && note.data.length) {
        rate.depositPolicy = new DepositPolicy(DepositPolicyType.DEFAULT_DEPOSIT, note.data[0]);
        return;
      }
      if (note.data && (typeof note.data === 'string')) {
        rate.depositPolicy = new DepositPolicy(DepositPolicyType.DEFAULT_DEPOSIT, note.data);
        return;
      }
    }

    if (rateData.rateTags) {
      const tags = rateData.rateTags;
      if (tags.indexOf('ONE_NIGHT_DEPOSIT_REQUIRED') !== -1) {
        rate.depositPolicy = new DepositPolicy(DepositPolicyType.ONE_NIGHT_DEPOSIT);
        return;
      }

      if (tags.indexOf('FULL_DEPOSIT_REQUIRED') !== -1) {
        rate.depositPolicy = new DepositPolicy(DepositPolicyType.FULL_DEPOSIT);
        return;
      }

      if (tags.indexOf('DEPOSIT_REQUIRED') !== -1 && tags.indexOf('NON_REFUNDABLE') === -1) {
        rate.depositPolicy = new DepositPolicy(DepositPolicyType.DEFAULT_DEPOSIT);
        return;
      }

      if (tags.indexOf('DEPOSIT_REQUIRED') !== -1 && tags.indexOf('NON_REFUNDABLE') !== -1) {
        rate.depositPolicy = new DepositPolicy(DepositPolicyType.FULL_DEPOSIT);
        return;
      }
    }
  }

  private _setRateChanges(rate: Rate, rateChanges: any) {
    for (const rateChange of rateChanges) {
      rate.rateChanges.push(new RateChange(rateChange.price, DateUtils.fromISOToDate(rateChange.startDate), DateUtils.fromISOToDate(rateChange.endDate)));
    }
  }

  private _setOriginalRateChanges(rate: Rate, rateChanges: any) {
    for (const rateChange of rateChanges) {
      rate.originalRateChanges.push(new RateChange(rateChange.price, DateUtils.fromISOToDate(rateChange.startDate), DateUtils.fromISOToDate(rateChange.endDate)));
    }
  }
}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/api/services/builders/reservation-model.builder.ts